<script setup lang="ts">
import PublicLayout from '@/components/PublicLayout.vue'
</script>

<template>
  <PublicLayout>
    <div class="max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        <!-- Header -->
        <div class="mb-6 sm:mb-8">
          <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-2">Terms and Conditions</h1>
          <p class="text-xs sm:text-sm text-gray-600">Last updated: January 2025</p>
        </div>

        <!-- Content -->
        <div class="prose prose-sm max-w-none space-y-6">
          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">1. Agreement</h2>
            <p class="text-gray-700 leading-relaxed">
              These Terms and Conditions ("Terms") govern your use of AutomaDoc Pro ("Service"), provided by
              PT Karunia Solusi Informatika ("Provider", "we", "us", or "our"). By using the Service, you agree
              to be bound by these Terms.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">2. Service Description</h2>
            <p class="text-gray-700 mb-3">AutomaDoc Pro is a cloud-based document automation platform that provides:</p>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li>Document generation from Lark Base records using Word templates</li>
              <li>PDF conversion capability</li>
              <li>Automatic saving to Lark attachment fields</li>
              <li>Bulk generation with ZIP download</li>
              <li>Background async processing for large batches</li>
              <li>Webhook notifications</li>
            </ul>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">3. Subscription and Fees</h2>
            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">3.1 Plans and Pricing</h3>
            <p class="text-gray-700 leading-relaxed">
              Service is provided on a subscription basis. Pricing and features vary by plan (Silver, Gold, Platinum).
              See our <a href="#/pricing" class="text-blue-600 hover:text-blue-800">Pricing page</a> for current rates.
            </p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">3.2 Payment Terms</h3>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li>Monthly fees are payable in advance</li>
              <li>Payment is due within 14 days of invoice date</li>
              <li>Overage fees for generations exceeding monthly quota are billed in arrears</li>
              <li>All fees are exclusive of applicable taxes</li>
            </ul>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">3.3 Late Payment</h3>
            <p class="text-gray-700 leading-relaxed">
              Overdue amounts accrue interest at 2% per month. Service may be suspended if payment is more than
              30 days overdue.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">4. Term and Termination</h2>
            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">4.1 Initial Term</h3>
            <p class="text-gray-700 leading-relaxed">
              Subscription begins on the date of agreement and continues for an initial term of 12 months.
            </p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">4.2 Renewal</h3>
            <p class="text-gray-700 leading-relaxed">
              Subscription automatically renews for successive 12-month periods unless either party provides
              written notice of non-renewal at least 30 days before the end of the current term.
            </p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">4.3 Termination</h3>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li>Either party may terminate for convenience with 30 days' written notice</li>
              <li>Either party may terminate immediately for material breach if not cured within 15 days</li>
              <li>Provider may suspend Service immediately for violation of Acceptable Use policy</li>
            </ul>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">5. Acceptable Use</h2>
            <p class="text-gray-700 mb-3">You agree not to:</p>
            <ul class="list-disc pl-6 space-y-2 text-gray-700">
              <li>Use the Service for any illegal, fraudulent, or defamatory purpose</li>
              <li>Generate documents containing illegal content, hate speech, or content violating third-party rights</li>
              <li>Attempt to reverse engineer, decompile, or access the Service's source code</li>
              <li>Interfere with or disrupt the Service's integrity or performance</li>
              <li>Share account credentials with unauthorized users</li>
              <li>Resell or redistribute the Service without Provider's written consent</li>
              <li>Exceed template limits specified in your plan</li>
            </ul>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">6. Service Level Agreement</h2>
            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">6.1 Uptime Commitment</h3>
            <p class="text-gray-700 leading-relaxed">
              Provider commits to monthly uptime as specified in your plan (99% for Silver, 99.5% for Gold and Platinum).
            </p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">6.2 Service Credits</h3>
            <p class="text-gray-700 leading-relaxed">
              If uptime falls below the commitment, you may request service credits as specified in your service agreement.
              Service credits are your sole remedy for downtime.
            </p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">6.3 Support</h3>
            <p class="text-gray-700 leading-relaxed">
              Technical support is provided via channels and response times specified in your plan. See our
              <a href="#/pricing" class="text-blue-600 hover:text-blue-800">Pricing page</a> for details.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">7. Data Protection</h2>
            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">7.1 Data Ownership</h3>
            <p class="text-gray-700 leading-relaxed">
              You retain all ownership rights to your data, templates, and generated documents. Provider processes
              your data solely to provide the Service.
            </p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">7.2 Data Security</h3>
            <p class="text-gray-700 leading-relaxed">
              Provider implements industry-standard security measures including encryption in transit (TLS 1.3),
              encryption at rest, and access controls. See our <a href="#/privacy" class="text-blue-600 hover:text-blue-800">Privacy Policy</a> for details.
            </p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">7.3 Data Retention</h3>
            <p class="text-gray-700 leading-relaxed">
              Data retention periods vary by plan. Upon termination, your data is available for download for 30 days,
              after which it is permanently deleted.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">8. Intellectual Property</h2>
            <p class="text-gray-700 leading-relaxed mb-3">
              Provider retains all rights to the Service, platform, and related intellectual property. You retain
              all rights to your templates and generated documents. This agreement grants no ownership rights to either party
              in the other's intellectual property.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">9. Warranties and Disclaimers</h2>
            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">9.1 Provider Warranties</h3>
            <p class="text-gray-700 leading-relaxed">
              Provider warrants that the Service will perform substantially as described in documentation and that
              Provider will use reasonable skill and care in providing the Service.
            </p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2 mt-4">9.2 Disclaimer</h3>
            <p class="text-gray-700 leading-relaxed">
              EXCEPT AS EXPRESSLY STATED, THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
              Provider is not responsible for accuracy or quality of generated documents. You are responsible for reviewing
              all output before use.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">10. Limitation of Liability</h2>
            <p class="text-gray-700 leading-relaxed">
              PROVIDER'S TOTAL LIABILITY SHALL NOT EXCEED THE MONTHLY FEE PAID IN THE THREE (3) MONTHS IMMEDIATELY
              PRECEDING THE EVENT. IN NO EVENT SHALL PROVIDER BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL,
              OR PUNITIVE DAMAGES.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">11. Governing Law</h2>
            <p class="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of the Republic of Indonesia. Disputes shall be resolved through
              good faith negotiation, mediation by BANI (Badan Arbitrase Nasional Indonesia), or litigation in the
              courts of South Jakarta, Indonesia.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">12. Changes to Terms</h2>
            <p class="text-gray-700 leading-relaxed">
              Provider may update these Terms upon 30 days' written notice. Continued use of the Service after changes
              take effect constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-800 mb-3">13. Contact Information</h2>
            <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-3">
              <p class="text-gray-800 font-semibold mb-2">PT Karunia Solusi Informatika</p>
              <p class="text-gray-700">Jl. Margasatwa Raya No. 17, Pondok Labu</p>
              <p class="text-gray-700">Jakarta Selatan 12450, Indonesia</p>
              <p class="text-gray-700 mt-2">Email: <a href="mailto:christophe@ksi-digital.com" class="text-blue-600 hover:text-blue-800">christophe@ksi-digital.com</a></p>
              <p class="text-gray-700">Website: <a href="https://www.ksi-digital.com" target="_blank" class="text-blue-600 hover:text-blue-800">www.ksi-digital.com</a></p>
            </div>
          </section>
        </div>
    </div>
  </PublicLayout>
</template>
